import { useState, useEffect, useRef, MutableRefObject } from 'react';
import { UserStuckModal } from 'src/common/utils/userStuckModal';
import FeedbackModal from 'src/common/utils/FeedbackModal';
import {
  Button,
  ButtonSet,
  Tooltip,
  ContentSwitcher,
  Switch,
  ModalWrapper,
  TileGroup,
  RadioTile,
  NumberInput,
} from 'carbon-components-react';
import { FormattedMessage } from 'react-intl';
import Cookies from 'js-cookie';
import Helmet from 'react-helmet';
import './TurbonomicPriceEstimator.scss';
import 'src/common/style/cds-data.scss';
import 'src/common/style/importantStyle.scss';
import 'src/common/style/common.scss';
import { segmentTracking, productTitle, pageUrl } from '../lib/tracker';
import { FormModal } from 'src/common/utils/FormModal';
import Thanks from './Thanks';
import { useEnv } from 'src/common/hooks/widgets/useEnv';
import { useBPName } from 'src/common/hooks/widgets/useBPName';
import { useCookiesURL } from 'src/common/hooks/widgets/useCookiesURL';
import { useCurrency } from 'src/common/hooks/widgets/useCurrency';
import { useSmallBreakpoint } from 'src/common/hooks/widgets/useBreakpoint';
import * as URX_FORM from '../constants/urx-form';
import { swapCountryAndLanguage } from '../utils/swapCountryAndLanguage';
import {
  UT30TurboPE,
  CloudInstances,
  limits,
  Product,
  VMS,
  emailCOSJson,
} from '../constants/defaults';
import messages from '../locales/messages';
import { IBMLocale } from 'src/common/locale/mapValidLocale';
import { setPrice } from '../hooks/usePrice';

import {
  determineCountry,
  LangDefaults,
  useLang,
  getMessage,
} from 'src/common/hooks/widgets/useLang';
import countryList from '../data/currencies.json';
import { initUrxForm } from 'src/common/hooks/widgets/urxForm';
import {
  urxScriptLoader,
  urxScript,
} from 'src/common/hooks/widgets/globalDefinitions';
import BusinessPartner from 'src/common/components/BusinessPartner/BusinessPartner';
import Feedback from 'src/common/utils/Feedback';

import useTimer from 'src/common/hooks/useTimer';
import { ramdomSelector } from 'src/common/lib/RandomSelector';

import DisclaimerText from 'src/common/components/DisclaimerText/DisclaimerText';

export interface TurbonomicPriceEstimatorProps {
  langCode: string;
  test: boolean;
  countryToTest: string;
}

export interface roiCookieContent {
  currentNumberOnPremHosts: number;
  currentMonthtlyCloudBill: number;
  currentCostOnPremHost: number;
  annualGrowthRateOnPremVM: number;
  annualGrowthRateCloudInstance: number;
  estimate: string;
}

export const cookieContentObject: roiCookieContent = {
  currentNumberOnPremHosts: 0,
  currentMonthtlyCloudBill: 0,
  currentCostOnPremHost: 0,
  annualGrowthRateOnPremVM: 0,
  annualGrowthRateCloudInstance: 0,
  estimate: '',
};

/**
 *
 * @param {*} param0
 * @returns
 */
const TurbonomicPriceEstimator = ({
  langCode,
  test,
  countryToTest,
}: TurbonomicPriceEstimatorProps) => {
  const localeCode = swapCountryAndLanguage(langCode as IBMLocale);
  const [cloudInstances, setCloudInstances] = useState(CloudInstances.default);
  const defaultYes = getMessage(messages.yes);
  const [interestedInContainers] = useState(defaultYes);
  const [onPremVMs, setOnPremVMs] = useState(VMS.default);
  const [cookieContent, setCookieContent] = useState(cookieContentObject);
  const [showContactUs, setShowContactUs] = useState(true);
  const [invalidVMMax, setInvalidVMMax] = useState(false);
  const [invalidVMMin, setInvalidVMMin] = useState(false);
  const [invalidCIMax, setInvalidCIMax] = useState(false);
  const [invalidCIMin, setInvalidCIMin] = useState(false);
  const [formComplete, setFormComplete] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isSmall = useSmallBreakpoint('560px');
  const [environment] = useEnv();
  const { bpName, companyId, logo, desc } = useBPName(UT30TurboPE, true);
  const cookieURL = useCookiesURL();
  const [productToAcquire, setProductToAcquire] = useState('cloud');
  const [currency, setCurrency] = useState('');
  const [country, setCountry] = useState('');
  const [closeModal, setCloseModal] = useState(false);
  const [useURXID, setUseURXID] = useState(URX_FORM.FORM_ID);
  //
  const [lang, userLocale] = useLang(
    countryList,
    test,
    countryToTest,
    setCurrency,
    setCountry,
    localeCode,
  );

  // Set us-en
  const [urxlang, setUrxLang] = useState(
    LangDefaults.localCountryLang as IBMLocale,
  );
  const [toggleCountry, setToggleCountry] = useState(false);
  const [modalLocalCountry, setModalLocalCountry] = useState('');
  // Set USD
  const [modalLocalCurrency, setModalLocalCurrency] = useState(
    LangDefaults.localCurrency,
  );
  // Set English language
  const [modalLocalLanguage, setModalLocalLanguage] = useState(
    LangDefaults.CountryLanguage,
  );
  const [estimatedPrice, setEstimatedPrice] = useCurrency(
    currency,
    userLocale,
    0,
  );
  window.onUrxFormSubmitSuccess = () => {
    setFormComplete(!formComplete);
    setDrawerOpen(false); // Hide Drawer on Thank you page
    setCloseModal(true);
  };

  const [openUserStuckModal, setOpenUserStuckModal] = useState(false);
  const [localExist, setLocalExist] = useState(false);
  let localStorageFound = localStorage.getItem('langLocale');
  if (localStorageFound === null)
    localStorageFound = LangDefaults.localCountryLang;
  const [selectedLocale, setSelectedLocale] = useState(localStorageFound);
  // Feedback controls, Modal User Help
  const [showForm, setShowForm] = useState(false);
  const [showPopupForm, setShowPopUpForm] = useState(false);
  const [tileValueSelected, setTileValueSelected] = useState('onprem_hybrid');
  const [, setMoveSlider] = useState(false);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [thumbsUp, setThumbsUp] = useState(true);
  const onPremVMsRef = useRef() as MutableRefObject<HTMLInputElement>;
  const cloudInstancesRef = useRef() as MutableRefObject<HTMLInputElement>;
  const cloudSpendRef = useRef() as MutableRefObject<HTMLInputElement>;
  const [cloudAmount, setCloudAmount] = useState(0);

  // Determine Country
  determineCountry(
    setLocalExist,
    setSelectedLocale,
    setModalLocalCountry,
    setModalLocalLanguage,
    setModalLocalCurrency,
    setToggleCountry,
    lang,
    countryList,
    true,
  );

  /**
   * Call to start the pricing
   */
  setPrice(
    cloudInstances,
    onPremVMs,
    setShowContactUs,
    setEstimatedPrice,
    productToAcquire,
    currency,
    country,
    userLocale,
    cloudAmount,
  );

  /**
   * Define validation of the Input field of the sliders
   */
  useEffect(() => {
    const vmsId = document.getElementById('VMS-Id-input-for-slider');
    const cloudId = document.getElementById('Cloud-Id-input-for-slider');

    /**
     * Define Listener
     * @param id
     * @param setMax
     * @param setMin
     * @param setvalue
     * @param max
     * @param min
     */
    const addListener = (
      id: HTMLElement | null,
      setMax: React.Dispatch<React.SetStateAction<boolean>>,
      setMin: React.Dispatch<React.SetStateAction<boolean>>,
      max: number,
      min: number,
      setValue: React.Dispatch<React.SetStateAction<number>>,
    ) => {
      if (id !== null) {
        id.addEventListener('input', (e) => {
          if (e != null) {
            const element = e.currentTarget as HTMLInputElement;
            if (element.value !== '') {
              if (Number.parseInt(element.value) > max) {
                element.value = max.toString();
                setMax(true);
              } else if (Number.parseInt(element.value) < min) {
                element.value = min.toString();
                setMin(true);
              }
            } else {
              setValue(min);
            }
            setMoveSlider(true);
          }
        });
      }
    };

    /**
     * VMs
     */
    addListener(
      vmsId,
      setInvalidVMMax,
      setInvalidVMMin,
      VMS.max,
      VMS.min,
      setOnPremVMs,
    );

    /**
     * Cloud Instances
     */
    addListener(
      cloudId,
      setInvalidVMMax,
      setInvalidCIMin,
      CloudInstances.max,
      CloudInstances.min,
      setCloudInstances,
    );
  }, []);

  /**
   * Read and set defaults from ROI
   */
  const startTxnProcessor = () => {
    try {
      setTimeout(async () => {
        const showBenefit = localStorage.getItem('showForm');
        if (showBenefit === 'true') {
          setShowForm(true);
        }
        const showPopupForm = localStorage.getItem('showPopupForm');
        if (showPopupForm === 'true') {
          setShowPopUpForm(true);
        }
        startTxnProcessor();
      }, limits.secs * 1000);
    } catch (err) {
      console.error(`Error while timeout: ${err}`);
    }
  };

  startTxnProcessor();

  /**
   *
   */
  useEffect(() => {
    const showPopupForm = localStorage.getItem('showPopupForm');
    if (showPopupForm === 'true') {
      setUseURXID(URX_FORM.FORM_ID);
      initUrxForm(
        environment,
        URX_FORM.INSTANCE_ID,
        URX_FORM.FORM_ID,
        urxlang,
        companyId,
      );
      setTimeout(
        async () => {
          setCookie(URX_FORM.FORM_ID);
          setOpenUserStuckModal(true);
          setShowPopUpForm(false);
          localStorage.setItem('showPopupForm', 'false');
        },
        bpName ? 1000 : 10,
      );
    }
    const showBenefit = localStorage.getItem('showForm');
    if (showBenefit === 'true') {
      setUseURXID(URX_FORM.FORM_ID);
      initUrxForm(
        environment,
        URX_FORM.INSTANCE_ID,
        URX_FORM.FORM_ID,
        urxlang,
        companyId,
      );
      setTimeout(
        async () => {
          setCookie(URX_FORM.FORM_ID);
          setDrawerOpen(true);
          setShowForm(false);
          localStorage.setItem('showForm', 'false');
        },
        bpName ? 1000 : 10,
      );
    }
  }, [showForm, showPopupForm]);

  const { timeout, resetTimer } = useTimer(false, 120000);

  useEffect(() => {
    if (timeout) {
      setCookie(URX_FORM.FORM_ID);
      setUseURXID(URX_FORM.FORM_ID);
      initUrxForm(
        environment,
        URX_FORM.INSTANCE_ID,
        URX_FORM.FORM_ID,
        urxlang,
        companyId,
      );
      setOpenUserStuckModal(true);
      setMoveSlider(false);
    }
  }, [timeout]);

  /**
   *
   */
  useEffect(() => {
    setCookie(URX_FORM.FORM_ID);
    setProductToAcquire('SaaS'); //trigger the initial calucation
    localStorage.setItem('VMS-Id', onPremVMs.toString());
    localStorage.setItem('Cloud-Id', cloudInstances.toString());
  }, [onPremVMs, cloudInstances, interestedInContainers, estimatedPrice]);

  /**
   *
   */
  const setCookie = (formId: string) => {
    const jsonCookie = localStorage.getItem('formContent');
    let cookieContent = '';
    if (jsonCookie !== null) {
      const jsonFileToProcess = JSON.parse(jsonCookie);
      cookieContent = jsonFileToProcess.message;
      setCookieContent(jsonFileToProcess.content);
    }
    const text = JSON.stringify({
      source: formId,
      // One field to provide all values (stringify)
      dcq: {
        Q_NOTESDATA:
          cookieURL +
          ' | Number On Prem VMs:' +
          onPremVMs +
          ' | Number of Cloud Instances: ' +
          cloudInstances +
          ' | Interested in Containers: ' +
          interestedInContainers +
          ' | Product to acquire: ' +
          productToAcquire +
          ' | Estimated price: ' +
          estimatedPrice +
          ' | Request a quote -IBM Turbonmic PE Estimator-' +
          ' | FormId:' +
          formId +
          ' | ROI feed: ' +
          cookieContent,
        Q_ASSET_NAME: 'IBM Turbonomic PE Estimator', // ISC - Asset Name
      },
    });
    Cookies.set('urxdcq', text);
  };

  /* Have urx form deault in us-en when on language us page */
  useEffect(() => {
    const fetchedUrxLang = localeCode; // us-en
    setUrxLang(userLocale as IBMLocale); // de-de
    // if de-de === de-de
    if (fetchedUrxLang !== lang) {
      setUrxLang(fetchedUrxLang as IBMLocale); // default us-en
    }
  });

  /**
   *
   * @param value
   * @param setValue
   * @param max
   * @param min
   */
  const handleSliders = (
    value: number,
    setValue: React.Dispatch<React.SetStateAction<number>>,
    max: number,
    min: number,
  ) => {
    resetTimer();
    if (value >= min && value <= max) {
      setValue(value);
    } else if (value >= max) {
      setValue(max);
    } else {
      setValue(min);
    }
  };

  const [btnText, setBtnText] = useState({
    id: '',
    defaultMessage: '',
  });

  useEffect(() => {
    setBtnText(
      ramdomSelector() === 1
        ? messages.getQuoteButton
        : messages.getQuoteButton2,
    );
  }, []);
  return (
    <div className="Turbo_PE dsh-widget widget-styles">
      <Helmet>
        <script type="application/javascript" src={urxScriptLoader}></script>
        <script type="application/javascript" src={urxScript}></script>
      </Helmet>
      {formComplete ? (
        <Thanks
          interestedInContainers={interestedInContainers}
          cloudInstances={cloudInstances}
          onPremVMs={onPremVMs}
          productToAcquire={productToAcquire}
          estimatedPrice={estimatedPrice}
          cookieContent={cookieContent}
          bpName={bpName}
          bpConsentMail={false}
        ></Thanks>
      ) : (
        // Not Form completed
        <div className="bx--grid widget-reset" id="TurboGrid">
          {bpName ? (
            <BusinessPartner bpName={bpName} logo={logo} desc={desc} />
          ) : null}
          <div className="bx--row mar3">
            <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-4">
              <div className="bx--row">
                <div className="bx--col-lg-4 bx--col-md-8 bx--col-sm-8 mar1 bx--offset-lg-12"></div>
              </div>
              <div className="bx--row">
                <div className="bx--col-lg-4 bx--col-md-8 bx--col-sm-8 mar1 bx--offset-lg-12">
                  {localExist && toggleCountry ? (
                    <ModalWrapper
                      modalLabel={` ${getMessage(
                        messages.foundCountry,
                      )} ${modalLocalCountry}.
                      ${getMessage(
                        messages.accessPage,
                      )} ${modalLocalLanguage} ${modalLocalCurrency}
                      `}
                      modalHeading={`${getMessage(messages.availableIn)}
                      ${modalLocalLanguage} ${getMessage(
                        messages.localCurrencie,
                      )} ${modalLocalCurrency}`}
                      size={'sm'}
                      primaryButtonText={`${getMessage(messages.takeMeThere)}`}
                      secondaryButtonText={`${getMessage(messages.stayHere)} `}
                      buttonTriggerText={` ${getMessage(
                        messages.accessPageCapitalized,
                      )} ${modalLocalLanguage}`}
                      handleOpen={() => {
                        segmentTracking('CTA Clicked', {
                          pageTitle: 'Turbonomic Pricing Page',
                          object: 'Pricing page',
                          resultValue: 'CTA clicked',
                          CTA: 'Modal open ' + modalLocalCountry,
                          location: 'Estimator',
                          action: '',
                          field: '',
                        });
                      }}
                      handleSubmit={() => {
                        localStorage.setItem('langLocale', selectedLocale);
                        (window.location.href =
                          'https://www.ibm.com/' +
                          selectedLocale +
                          '/products/turbonomic/pricing'),
                          segmentTracking('CTA Clicked', {
                            pageTitle: 'Turbonomic Pricing Page',
                            object: 'Pricing page',
                            resultValue: 'CTA clicked',
                            CTA: 'Switched to country ' + modalLocalCountry,
                            location: 'Estimator',
                            action: '',
                            field: '',
                          });
                        return true;
                      }}
                      onSecondarySubmit={() => {
                        segmentTracking('CTA Clicked', {
                          pageTitle: 'Turbonomic Pricing Page',
                          object: 'Pricing page',
                          resultValue: 'CTA clicked',
                          CTA: 'Stay here button clicked ' + modalLocalCountry,
                          location: 'Estimator',
                          action: '',
                          field: '',
                        });
                        setToggleCountry(false);
                        return true;
                      }}
                    ></ModalWrapper>
                  ) : null}
                </div>
              </div>
              <div className="bx--row">
                <div className="bx--col-lg-9 bx--col-md-16 bx--col-sm-16">
                  <div className="bx--row">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <p className="price_table_comp__price">
                        <FormattedMessage {...messages.widgetText_1} />
                      </p>
                    </div>
                  </div>
                  <div className="bx--row padding_24_3">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <p className="page_body">
                        <FormattedMessage {...messages.widgetInfo} />
                      </p>
                      <p className="price_table_comp__price padding_24_3">
                        <FormattedMessage {...messages.question_title} />
                      </p>
                    </div>
                  </div>
                  {/* Cloud only or on prem/hybrid selection */}
                  <div className="bx--row padding_24_3">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <TileGroup
                        className="Turbo_PE__tilegroup"
                        name="radio tile group"
                        valueSelected={tileValueSelected}
                      >
                        <RadioTile
                          id="radio-tile-2-turbo_PE"
                          value="onprem_hybrid"
                          className="Turbo_PE__tile"
                          onClick={() => {
                            setShowContactUs(true);
                            setProductToAcquire('SaaS');
                            setTileValueSelected('onprem_hybrid');
                            segmentTracking('CTA Clicked', {
                              pageTitle: 'Turbonomic Pricing Page',
                              object: 'Pricing page',
                              resultValue: 'CTA clicked',
                              CTA: `On Prem/ Hybrid tile selected`,
                              location: 'Estimator',
                              action: '',
                              field: '',
                            });
                          }}
                        >
                          <div className="page_sub_heading mar9">
                            <FormattedMessage
                              {...messages.onprem_hybrid_tile}
                            />
                          </div>
                        </RadioTile>
                        <RadioTile
                          className="Turbo_PE__tile"
                          id="radio-tile-1-turbo_PE"
                          value="cloudonly"
                          onClick={() => {
                            setShowContactUs(true);
                            setProductToAcquire('cloud');
                            setTileValueSelected('cloudonly');
                            segmentTracking('CTA Clicked', {
                              pageTitle: 'Turbonomic Pricing Page',
                              object: 'Pricing page',
                              resultValue: 'CTA clicked',
                              CTA: `Cloud only tile selected`,
                              location: 'Estimator',
                              action: '',
                              field: '',
                            });
                          }}
                        >
                          <div className="page_sub_heading mar9">
                            <FormattedMessage {...messages.cloud_tile} />
                          </div>
                        </RadioTile>
                      </TileGroup>
                    </div>
                  </div>
                  {/*  Product Acquire */}
                  {tileValueSelected === 'onprem_hybrid' ? (
                    <div>
                      <div className="bx--row padding_24_3">
                        <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16 mar5">
                          <Tooltip
                            className=""
                            direction={'bottom'}
                            tabIndex={0}
                            triggerText={
                              <span className="page_body">
                                <FormattedMessage
                                  {...messages.productToAcquire}
                                />
                              </span>
                            }
                            onChange={() => {
                              segmentTracking('CTA Clicked', {
                                pageTitle: 'Turbonomic Pricing Page',
                                object: 'Pricing page',
                                resultValue: 'CTA clicked',
                                CTA: 'Acquire tooltip',
                                location: 'Estimator',
                                action: '',
                                field: '',
                              });
                            }}
                          >
                            {productToAcquire === 'SaaS' ? (
                              <span className="slide_text">
                                <FormattedMessage
                                  {...messages.inlineTextTitleSaaS}
                                />
                                <br />
                                <FormattedMessage
                                  {...messages.inlineTextSaaS}
                                />
                              </span>
                            ) : (
                              <span className="slide_text">
                                <FormattedMessage
                                  {...messages.inlineTextTitleLicense}
                                />
                                <br />
                                <FormattedMessage
                                  {...messages.inlineTextLicense}
                                />
                              </span>
                            )}
                          </Tooltip>
                        </div>
                      </div>
                      <div className="bx--row mar1">
                        <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                          <ContentSwitcher
                            selectionMode="manual"
                            onChange={(obj) => {
                              const { name, text } = obj;
                              setProductToAcquire(
                                name === undefined ? '' : name + '',
                              );
                              segmentTracking('CTA Clicked', {
                                pageTitle: 'Turbonomic Pricing Page',
                                object: 'Pricing page',
                                resultValue: `CTA clicked ${text}`,
                                CTA: `Acquire product selected`,
                                location: 'Estimator',
                                action: '',
                                field: '',
                              });
                            }}
                          >
                            {country !== 'CHN' ? (
                              <Switch name={`${Product.SaaS}`} text="SaaS" />
                            ) : (
                              <></>
                            )}
                            <Switch
                              name={`${Product.Software}`}
                              text="Software"
                            />
                          </ContentSwitcher>
                        </div>
                      </div>
                      {/*  Total Number of On-Prem VMs */}
                      <div className="bx--row">
                        <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                          <Tooltip
                            onChange={() => {
                              segmentTracking('CTA Clicked', {
                                pageTitle: 'Turbonomic Pricing Page',
                                object: 'Pricing Page',
                                resultValue: 'CTA clicked',
                                CTA: 'VM tooltip',
                                location: 'Estimator',
                                action: '',
                                field: '',
                              });
                            }}
                            direction={'bottom'}
                            tabIndex={0}
                            triggerText={
                              <span className="page_body">
                                <FormattedMessage
                                  {...messages.labelTotalOnPremVms}
                                />
                              </span>
                            }
                          >
                            <span className="slide_text">
                              <FormattedMessage
                                {...messages.totalOnPremiseHeadTooltip}
                              />
                              <br />
                              <FormattedMessage
                                {...messages.totalOnPremiseDescTooltip}
                              />
                            </span>
                          </Tooltip>
                        </div>
                      </div>
                      {/* Number Input Total On_prem VMs */}
                      <div className="bx--row space_half_top">
                        <div className="bx--col-lg-8 bx--col-md-8 bx--col-sm-16">
                          <NumberInput
                            id="VMS-Id"
                            size="md"
                            allowEmpty={true}
                            ref={onPremVMsRef}
                            className="number-input"
                            min={VMS.min}
                            max={VMS.max}
                            value={onPremVMs}
                            invalid={invalidVMMax || invalidVMMin}
                            helperText={
                              invalidVMMax ? (
                                <span className="slide_text">
                                  <FormattedMessage
                                    {...messages.invalidVmText_1}
                                  />
                                  {VMS.max}
                                  <FormattedMessage
                                    {...messages.invalidVmText_2}
                                  />
                                </span>
                              ) : invalidVMMin ? (
                                <span className="slide_text">
                                  <FormattedMessage
                                    {...messages.invalidVmText_3}
                                  />
                                  {VMS.min}
                                  <FormattedMessage
                                    {...messages.invalidVmText_2}
                                  />
                                </span>
                              ) : null
                            }
                            onChange={() => {
                              if (
                                Number(onPremVMsRef.current.value) > VMS.max
                              ) {
                                setOnPremVMs(VMS.max);
                                setInvalidVMMax(true);
                              } else if (
                                Number(onPremVMsRef.current.value) < VMS.min
                              ) {
                                setOnPremVMs(VMS.min);
                                setInvalidVMMin(true);
                              } else {
                                handleSliders(
                                  Number(onPremVMsRef.current.value),
                                  setOnPremVMs,
                                  VMS.max,
                                  VMS.min,
                                );
                                setInvalidVMMax(false);
                                setInvalidVMMin(false);
                              }
                              segmentTracking('CTA Clicked', {
                                pageTitle: 'Turbonomic Pricing Page',
                                object: 'Pricing Page',
                                resultValue: 'CTA clicked',
                                CTA: 'VM input',
                                location: 'Estimator',
                                action: '',
                                field: '',
                              });
                              setMoveSlider(true);
                            }}
                          />
                        </div>
                      </div>
                      {/*  Number Input Total Cloud Instances */}
                      <div className="bx--row mar3">
                        <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                          <Tooltip
                            onChange={() => {
                              segmentTracking('CTA Clicked', {
                                pageTitle: 'Turbonomic Pricing Page',
                                object: 'Pricing Page',
                                resultValue: 'CTA clicked',
                                CTA: 'Cloud Instances tooltip',
                                location: 'Estimator',
                                action: '',
                                field: '',
                              });
                            }}
                            direction={isSmall ? 'left' : 'bottom'}
                            tabIndex={0}
                            triggerText={
                              <span className="page_body">
                                <FormattedMessage
                                  {...messages.labelTotalCloudInstances}
                                />
                              </span>
                            }
                          >
                            <span className="slide_text">
                              <FormattedMessage
                                {...messages.totalCloudInstancesHeadTooltip}
                              />
                              <br />
                              <FormattedMessage
                                {...messages.totalCloudInstancesDescTooltip}
                              />
                            </span>
                          </Tooltip>
                        </div>
                      </div>
                      <div className="bx--row space_half_top mar2">
                        <div className="bx--col-lg-8 bx--col-md-8 bx--col-sm-16">
                          <NumberInput
                            id="Cloud-Id"
                            size="md"
                            allowEmpty={true}
                            ref={cloudInstancesRef}
                            className="number-input"
                            min={CloudInstances.min}
                            max={CloudInstances.max}
                            value={cloudInstances}
                            invalid={invalidCIMax || invalidCIMin}
                            helperText={
                              invalidCIMax === true ? (
                                <span className="slide_text">
                                  <FormattedMessage
                                    {...messages.invalidVmText_1}
                                  />
                                  {VMS.max}
                                  <FormattedMessage
                                    {...messages.invalidVmText_2}
                                  />
                                </span>
                              ) : invalidCIMin === true ? (
                                <span className="slide_text">
                                  <FormattedMessage
                                    {...messages.invalidVmText_3}
                                  />
                                  {VMS.min}
                                  <FormattedMessage
                                    {...messages.invalidVmText_2}
                                  />
                                </span>
                              ) : null
                            }
                            onChange={() => {
                              if (
                                Number(cloudInstancesRef.current.value) >
                                CloudInstances.max
                              ) {
                                setCloudInstances(CloudInstances.max);
                                setInvalidCIMax(true);
                              } else if (
                                Number(cloudInstancesRef.current.value) <
                                CloudInstances.min
                              ) {
                                setCloudInstances(CloudInstances.min);
                                setInvalidCIMin(true);
                              } else {
                                handleSliders(
                                  Number(cloudInstancesRef.current.value),
                                  setCloudInstances,
                                  CloudInstances.max,
                                  CloudInstances.min,
                                );
                                setInvalidCIMax(false);
                                setInvalidCIMin(false);
                              }
                              segmentTracking('CTA Clicked', {
                                pageTitle: 'Turbonomic Pricing Page',
                                object: 'Pricing Page',
                                resultValue: 'CTA clicked',
                                CTA: 'Cloud Instances input',
                                location: 'Estimator',
                                action: '',
                                field: '',
                              });
                              setMoveSlider(true);
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="bx--row padding_24_3">
                        <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                          <p className="price_table_comp__price">
                            <FormattedMessage {...messages.question_cloud} />
                          </p>
                        </div>
                      </div>
                      <div className="bx--row padding_24_3 mar2">
                        <div className="bx--col-lg-8 bx--col-md-8 bx--col-sm-16">
                          <NumberInput
                            id="sites"
                            size="md"
                            step={CloudInstances.incrementStep}
                            allowEmpty={true}
                            ref={cloudSpendRef}
                            invalid={false}
                            className="number-input"
                            min={0}
                            value={cloudAmount}
                            onChange={() => {
                              segmentTracking('CTA Clicked', {
                                pageTitle: 'Turbonomic Pricing Page',
                                object: 'Pricing Page',
                                resultValue: 'CTA clicked',
                                CTA: `Cloud Spend Input`,
                                location: 'Estimator',
                                action: '',
                                field: '',
                              });
                              setCloudAmount(
                                Number(cloudSpendRef.current.value),
                              );
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <div id="footnote" className="div-only-l mar3">
                      <div className="bx--row">
                        <div className="bx--col-lg-16 marginleft_1">
                          <div>
                            <p>
                              <span className="footnote_num">1</span>
                            </p>
                          </div>
                          <div className="footnote_text">
                            <DisclaimerText
                              localeCode={localeCode}
                              product={'Turbonomic'}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* <Feedback
                      pageTitle="Turbonomic Pricing Page"
                      object="Pricing Page"
                      messages={messages}
                      productTitle={productTitle}
                      pageUrl={pageUrl}
                      emailJson={emailCOSJson}
                    ></Feedback> */}
                  </div>
                </div>
                <div className="bx--col-lg-1"></div>
                <div className="bx--col-lg-5 bx--col-md-16 bx--col-sm-16 turbo-space">
                  <div className="bx-row" id="stickyNav">
                    <div className="Turbo_PE__sticky-nav price_table_comp">
                      {/* Condition to show table with results only if onprem/hybrid tile is selected */}
                      {tileValueSelected === 'onprem_hybrid' && (
                        <table className="cds--data-table cds--data-table--sm mar5">
                          <thead>
                            <tr>
                              <th scope="col" colSpan={2}>
                                <div className="price_table_comp__heading">
                                  <FormattedMessage
                                    {...messages.productToAcquireHeadTooltip}
                                  />
                                </div>
                              </th>
                              <th> </th>
                              <th scope="col">
                                <div className="price_table_comp__heading">
                                  <FormattedMessage {...messages.selection} />
                                </div>
                              </th>
                            </tr>
                          </thead>
                          <tbody aria-live="polite">
                            <tr>
                              <td colSpan={2}>
                                {' '}
                                <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                  <FormattedMessage
                                    {...messages.totalOnPremVvmsTitle}
                                  />
                                </div>
                              </td>
                              <td></td>
                              <td>
                                <div className="price_table_comp__quantity padding_8">
                                  {`${onPremVMs}`}
                                </div>
                              </td>
                            </tr>
                            <tr>
                              <td colSpan={2}>
                                <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                  <FormattedMessage
                                    {...messages.totalCloudInstancesTitle}
                                  />
                                </div>
                              </td>
                              <td></td>
                              <td>
                                <div className="price_table_comp__quantity padding_8">{`${cloudInstances}`}</div>
                              </td>
                            </tr>
                            <tr>
                              <td colSpan={2}>
                                <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                  <FormattedMessage
                                    {...messages.productToAcquireHeadTooltip}
                                  />
                                </div>
                              </td>
                              <td></td>
                              <td>
                                <div className="price_table_comp__quantity padding_8">{`${productToAcquire}`}</div>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      )}
                      {/* Condition if above ceratin value to contact-us button */}
                      {showContactUs === true ? (
                        <div id="contact_toggle">
                          <p
                            id="widget.contact_info"
                            className="price_table_comp__disclaimer mar4"
                          >
                            {tileValueSelected === 'cloudonly' ? (
                              <p className="price_table_comp__disclaimer">
                                <FormattedMessage
                                  {...messages.contactInfo_cloud}
                                />
                                <span className="pb">
                                  <FormattedMessage
                                    {...messages.contactInfo_cloud_1}
                                  />
                                </span>
                                <FormattedMessage
                                  {...messages.contactInfo_cloud_2}
                                />
                              </p>
                            ) : cloudInstances + onPremVMs > limits.max ? (
                              <FormattedMessage {...messages.contactInfo2} />
                            ) : (
                              <FormattedMessage {...messages.contactInfo} />
                            )}
                          </p>

                          <ButtonSet className="Turbo_PE__stack-button btn-set mar4">
                            <Button
                              id="contactus_pe_btn"
                              onClick={() => {
                                segmentTracking('CTA Clicked', {
                                  pageTitle: 'Turbonomic Pricing Page',
                                  object: 'Pricing Page',
                                  resultValue: 'CTA clicked',
                                  CTA: 'Contact us',
                                  location: 'Estimator',
                                  action: '',
                                  field: '',
                                });
                                setUseURXID(URX_FORM.CONTACT_SALES_ID);
                                initUrxForm(
                                  environment,
                                  URX_FORM.INSTANCE_ID,
                                  URX_FORM.CONTACT_SALES_ID,
                                  urxlang,
                                  companyId,
                                );
                                setDrawerOpen(true);
                                setCookie(URX_FORM.CONTACT_SALES_ID);
                              }}
                            >
                              <FormattedMessage {...messages.contactUs} />
                            </Button>
                            &nbsp;
                            <Button
                              size="sm"
                              id="contactus_roi_btn"
                              href="#Turbo_ROI"
                              target="_self"
                              rel="noreferrer"
                              kind="secondary"
                              onClick={() => {
                                segmentTracking('CTA Clicked', {
                                  pageTitle: 'Turbonomic Pricing Page',
                                  object: 'Pricing Page',
                                  resultValue: 'CTA clicked',
                                  CTA: 'View Turbonomic benefits button',
                                  location: 'Estimator',
                                  action: '',
                                  field: '',
                                });
                                setCookie(URX_FORM.FORM_ID);
                              }}
                            >
                              <FormattedMessage {...messages.getROIButton} />
                            </Button>
                          </ButtonSet>
                        </div>
                      ) : (
                        //Display Value if condition not met
                        <div id="price_toggle">
                          <p
                            id="widget.estimated-cost"
                            className="Turbo_PE__ibm-type-e price_table_comp__bodycopy"
                          >
                            <FormattedMessage
                              {...messages.calculationMessage}
                            />
                            <sup className="Turbo_PE__sup">1</sup>
                          </p>
                          <p className="padding_8 price_table_comp__price_lg_bold">
                            {`${estimatedPrice}`}
                            <sup className="Turbo_PE__sup">*</sup>
                          </p>
                          <p className="price_table_comp__disclaimer price_table_comp_color padding_24_1">
                            <sup className="Turbo_PE__sup">*</sup>
                            <FormattedMessage {...messages.asteriskPrice} />
                          </p>
                          <ButtonSet className="Turbo_PE__stack-button">
                            <Button
                              id="request_pe_btn"
                              onClick={() => {
                                segmentTracking('CTA Clicked', {
                                  pageTitle: 'Turbonomic Pricing Page',
                                  object: 'Pricing Page',
                                  resultValue: 'CTA clicked',
                                  CTA: btnText.defaultMessage,
                                  location: 'Estimator',
                                  action: '',
                                  field: '',
                                });
                                setUseURXID(URX_FORM.FORM_ID);
                                initUrxForm(
                                  environment,
                                  URX_FORM.INSTANCE_ID,
                                  URX_FORM.FORM_ID,
                                  urxlang,
                                  companyId,
                                );
                                setDrawerOpen(true);
                                setCookie(URX_FORM.FORM_ID);
                              }}
                            >
                              <FormattedMessage {...btnText} />
                            </Button>
                            <Button
                              size="sm"
                              id="request_roi_btn"
                              href="#Turbo_ROI"
                              target="_self"
                              rel="noreferrer"
                              kind="secondary"
                              onClick={() => {
                                segmentTracking('CTA Clicked', {
                                  pageTitle: 'Turbonomic Pricing Page',
                                  object: 'Pricing Page',
                                  resultValue: 'CTA clicked',
                                  CTA: 'View Turbonomic benefits button',
                                  location: 'Estimator',
                                  action: '',
                                  field: '',
                                });
                                setCookie(URX_FORM.FORM_ID);
                              }}
                            >
                              <FormattedMessage {...messages.getROIButton} />
                            </Button>
                          </ButtonSet>
                        </div>
                      )}
                      {/* End display results */}
                    </div>
                  </div>
                </div>
              </div>
              <hr className="planning_analytics__custom-hr" id="hr_row_two" />
            </div>
          </div>
        </div>
      )}
      <FormModal
        pageTitle="Turbonomic PE Page"
        object="Turbonomic PE Page"
        productTitle={'Turbonomic PE'}
        pageUrl={''}
        location={'Estimator'}
        bpName={bpName}
        logo={logo}
        drawerOpen={drawerOpen}
        setDrawerOpen={setDrawerOpen}
        setCloseModal={setCloseModal}
        closeModal={closeModal}
        formId={URX_FORM.FORM_ID}
        lang={lang}
      ></FormModal>
      <FeedbackModal
        pageTitle="Turbonomic PE Page"
        object="Turbonomic PE Page"
        messages={messages}
        productTitle={'Turbonomic PE'}
        pageUrl={''}
        emailJson={emailCOSJson}
        thumbsUp={thumbsUp}
        showFeedbackModal={feedbackModalOpen}
        setShowFeedbackModal={setFeedbackModalOpen}
      ></FeedbackModal>
      <UserStuckModal
        setShowResponseModal={setOpenUserStuckModal}
        showResponseModal={openUserStuckModal}
        textResponse={[
          `${getMessage(messages.userStuckModalText)}`,
          `${getMessage(messages.userStuckModalPrimaryButton)}`,
          `${getMessage(messages.userStuckModaSecondaryButton)}`,
        ]}
        primaryButton={`${getMessage(messages.modalContinue)}`}
        modalTitle={' '}
        tracking={[
          'Turbonomic Pricing Page',
          'Pricing Page',
          productTitle,
          pageUrl,
          emailCOSJson,
          bpName,
          logo,
        ]}
        messages={messages}
        setThumbsUp={setThumbsUp}
        setDrawerOpen={setDrawerOpen}
        setShowFeedbackModal={setFeedbackModalOpen}
      />
    </div>
  );
};

export default TurbonomicPriceEstimator;
